import { computed, ref, watch } from 'vue';
import { requestJSON } from '../core/http';
import { getStaticHostUrl } from '../core/static-host';
import { createLogger } from '@shared/utils/logger';
import type { Category, Product } from '@shared/types/domain';

const log = createLogger('ClientUseMenu');

type QuickFilter = {
  id: string;
  kind: string;
  source_id?: string | null;
  slug: string;
  name: string;
  sort_order: number;
};

type RuntimeMenuResponse = {
  taxonomy?: { categories?: Array<Record<string, any>> };
  catalog_items?: Product[];
  quick_filters?: QuickFilter[];
};

type HomeSnapshot = {
  placement?: string;
  revision?: string;
  version?: number;
  modules?: Array<{
    id?: string;
    slug?: string;
    title?: string;
    module_type?: string;
    items?: Product[];
  }>;
};

function normalizeCategoryId(id: unknown): string {
  return String(id ?? '').trim();
}

function snapshotUrl(): string {
  const staticHost = getStaticHostUrl();
  const path = '/assets/enfa/menu/home-published.json';
  return staticHost ? `${staticHost}${path}` : path;
}

export function useMenu() {
  const categories = ref<Category[]>([]);
  const catalogItems = ref<Product[]>([]);
  const quickFilters = ref<QuickFilter[]>([]);
  const homeSnapshot = ref<HomeSnapshot | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const searchTerm = ref('');
  const selectedCategoryId = ref<string | null>(null);
  const selectedQuickFilter = ref<string | null>(null);
  const showOnlyAvailable = ref(false);

  const categoryById = computed(() => {
    const map = new Map<string, Category>();
    for (const category of categories.value) {
      map.set(normalizeCategoryId(category.id), category);
    }
    return map;
  });

  const fetchMenu = async () => {
    loading.value = true;
    error.value = null;
    try {
      const runtime = await requestJSON<RuntimeMenuResponse>('/api/v1/menu/public');
      const taxonomy = runtime?.taxonomy?.categories || [];
      const catalog = Array.isArray(runtime?.catalog_items) ? runtime.catalog_items : [];
      const categoryRows: Category[] = taxonomy.map((row) => ({
        id: String(row.id || ''),
        name: String(row.name || ''),
        slug: row.slug ? String(row.slug) : undefined,
        display_order: Number(row.sort_order || 0),
        is_active: true,
        subcategories: Array.isArray(row.subcategories)
          ? row.subcategories.map((sub: Record<string, any>) => ({
              id: String(sub.id || ''),
              slug: String(sub.slug || ''),
              name: String(sub.name || ''),
              sort_order: Number(sub.sort_order || 0),
            }))
          : [],
        items: [],
      }));

      const categoryMap = new Map<string, Category>();
      for (const cat of categoryRows) {
        categoryMap.set(normalizeCategoryId(cat.id), cat);
      }

      for (const item of catalog) {
        const categoryId = normalizeCategoryId(item.menu_category_id || item.category_id);
        const category = categoryMap.get(categoryId);
        if (!category) continue;
        if (!Array.isArray(category.items)) {
          category.items = [];
        }
        category.items.push(item);
      }

      categories.value = categoryRows;
      catalogItems.value = catalog;
      quickFilters.value = Array.isArray(runtime?.quick_filters) ? runtime.quick_filters : [];

      try {
        homeSnapshot.value = await requestJSON<HomeSnapshot>(snapshotUrl(), { method: 'GET' });
      } catch {
        homeSnapshot.value = { modules: [] };
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error cargando menú';
      log.error('Error', err);
    } finally {
      loading.value = false;
    }
  };

  const filteredProducts = computed(() => {
    const products: Array<Product & { categoryName: string; categoryRepresentativeId: string }> = [];

    for (const category of categories.value) {
      const catId = normalizeCategoryId(category.id);
      if (selectedCategoryId.value && catId !== selectedCategoryId.value) continue;
      const items = Array.isArray(category.items) ? category.items : [];
      for (const item of items) {
        if (showOnlyAvailable.value && !item.is_available) continue;
        if (searchTerm.value) {
          const term = String(searchTerm.value ?? '').toLowerCase();
          const matchesName = String(item.name || '').toLowerCase().includes(term);
          const matchesDesc = String(item.description || '').toLowerCase().includes(term);
          if (!matchesName && !matchesDesc) continue;
        }
        if (selectedQuickFilter.value) {
          const filter = quickFilters.value.find((entry) => entry.id === selectedQuickFilter.value);
          if (filter) {
            if (filter.kind === 'label') {
              const labels = item.labels || [];
              if (!labels.some((label) => String(label.id) === String(filter.source_id))) {
                continue;
              }
            }
            if (filter.kind === 'item_kind' && item.item_kind !== filter.slug) {
              continue;
            }
          }
        }

        products.push({
          ...item,
          categoryName: category.name,
          categoryRepresentativeId: catId,
        });
      }
    }

    return products;
  });

  const availableCategories = computed(() =>
    categories.value
      .filter((c) => Array.isArray(c.items) && c.items.length > 0)
      .map((c) => ({ id: String(c.id), name: c.name }))
  );

  const homeModules = computed(() => {
    const modules = homeSnapshot.value?.modules;
    return Array.isArray(modules) ? modules : [];
  });

  // ── V3.2 client-side slice (fallback when no category selected) ──
  const visibleLimit = ref(24);
  const visibleProducts = computed(() => filteredProducts.value.slice(0, visibleLimit.value));
  const loadMore = () => { visibleLimit.value += 24; };
  watch(selectedCategoryId, () => { visibleLimit.value = 24; });

  // ── V3.4 paginated API (category-specific fetch) ──────────────
  const paginatedItems = ref<Product[]>([]);
  const paginatedPage = ref(1);
  const paginatedHasMore = ref(false);
  const paginatedLoading = ref(false);
  const paginatedTotal = ref(0);
  const PAGINATED_LIMIT = 24;

  const fetchCategoryPage = async (categoryId: string, page = 1) => {
    paginatedLoading.value = true;
    try {
      const url = `/api/v1/menu/public?category=${encodeURIComponent(categoryId)}&page=${page}&limit=${PAGINATED_LIMIT}`;
      const resp = await requestJSON<{ items: Product[]; total: number; has_more: boolean; page: number }>(url);
      const items = resp?.items ?? [];
      if (page === 1) {
        paginatedItems.value = items;
      } else {
        paginatedItems.value = [...paginatedItems.value, ...items];
      }
      paginatedTotal.value = resp?.total ?? 0;
      paginatedHasMore.value = resp?.has_more ?? false;
      paginatedPage.value = page;
    } catch (err) {
      log.warn('Paginated fetch failed, falling back to local filter', err);
    } finally {
      paginatedLoading.value = false;
    }
  };

  const loadNextPage = async () => {
    if (paginatedHasMore.value && selectedCategoryId.value) {
      await fetchCategoryPage(selectedCategoryId.value, paginatedPage.value + 1);
    }
  };

  // Trigger paginated fetch when a specific category is selected
  watch(selectedCategoryId, async (newCatId) => {
    visibleLimit.value = 24;
    paginatedItems.value = [];
    paginatedPage.value = 1;
    paginatedHasMore.value = false;
    if (newCatId) {
      await fetchCategoryPage(String(newCatId), 1);
    }
  });

  return {
    categories,
    categoryById,
    catalogItems,
    quickFilters,
    homeSnapshot,
    homeModules,
    loading,
    error,
    searchTerm,
    selectedCategoryId,
    selectedQuickFilter,
    showOnlyAvailable,
    filteredProducts,
    visibleProducts,
    visibleLimit,
    availableCategories,
    fetchMenu,
    loadMore,
    // V3.4 paginated
    paginatedItems,
    paginatedPage,
    paginatedHasMore,
    paginatedLoading,
    paginatedTotal,
    loadNextPage,
    fetchCategoryPage,
  };
}
